import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { type RefObject, useMemo, useRef } from "react"
import Draggable, { type DraggableData, type DraggableEvent } from "react-draggable"

type RpeSliderProps = {
  isRpe: boolean
  value: number | null
  setValue: (value: number | null) => void
  className?: string
} & React.ComponentProps<"div">

const SLIDER_WIDTH = 460
const SLIDER_STEP = SLIDER_WIDTH / 10
const SLIDER_COLORS = [
  "#47F255",
  "#9AF247",
  "#B6F247",
  "#DDF247",
  "#F2E747",
  "#F2D047",
  "#F2AB47",
  "#F27A47",
  "#F24747",
  "#E91010",
]

export function RpeSlider({ isRpe, value, setValue, className, ...props }: RpeSliderProps) {
  const childRef = useRef<HTMLDivElement>(null)

  const positionRound = useMemo(() => {
    if (value !== 0 && value !== null) {
      return (value - 1) * SLIDER_STEP - 16
    } else return -16
  }, [value])

  const handleDrag = (_: DraggableEvent, data: DraggableData) => {
    if (data.x !== undefined) {
      setValue(Math.round((data.x + 16) / SLIDER_STEP + 1))
    }
  }

  const setRpe = (rpe: number) => {
    if (isRpe) {
      setValue(rpe)
    }
  }

  return (
    <div className={cn("relative pt-4", className)} style={{ width: SLIDER_WIDTH }} {...props}>
      <Draggable
        grid={[SLIDER_STEP, 0]}
        axis="x"
        position={{ x: positionRound, y: 20 }}
        onDrag={handleDrag}
        bounds={{ left: -16, right: SLIDER_WIDTH - 16 - SLIDER_STEP }}
        disabled={!isRpe}
        nodeRef={childRef as RefObject<HTMLElement>}
      >
        <div className="relative z-1" style={{ marginLeft: SLIDER_STEP / 2, width: SLIDER_STEP }} ref={childRef}>
          <div
            className={cn(
              "flex h-8.5 w-8.5 cursor-grab items-center justify-center rounded-full",
              isRpe ? "bg-neutral-900" : "bg-neutral-700",
            )}
          >
            <Text fontStyle="BM" className="cursor-grab text-neutral-100">
              {value ?? 1}
            </Text>
          </div>

          <div className={cn("absolute left-4 h-6 w-0 border", isRpe ? "border-neutral-900" : "border-neutral-700")} />
        </div>
      </Draggable>

      <div className="mt-4 flex flex-col justify-center">
        <div className="flex w-full flex-row items-center justify-around overflow-hidden rounded-3xl">
          {Array.from({ length: 10 }).map((_, index) => (
            <div
              key={index}
              onClick={() => setRpe(index + 1)}
              style={{
                backgroundColor: isRpe ? SLIDER_COLORS[index] : "var(--neutral-400)",
                height: 20,
                width: SLIDER_STEP,
              }}
              className="relative cursor-pointer overflow-visible"
            />
          ))}
        </div>

        <div className="mt-2 flex justify-between text-center">
          <Text fontStyle="BM" className="text-textDefault-heading" style={{ width: SLIDER_STEP }}>
            1
          </Text>
          <Text fontStyle="BM" className="text-textDefault-heading" style={{ width: SLIDER_STEP }}>
            10
          </Text>
        </div>
      </div>
    </div>
  )
}
